import React, { useState, useEffect } from 'react';
import { Star, Check, MapPin, Clock, Shield, ArrowRight, Sparkles, Gift } from 'lucide-react';
import { Button } from './ui/button';
import BookingCalendar from './BookingCalendar';
import { Seo } from './Seo';
import { trackPixel } from '../lib/pixel';

const OFFERS = [
  {
    icon: Gift,
    title: 'First Visit — 5% Off',
    text: 'Automatic discount on your first booking. No code needed.',
    highlight: 'Applied automatically',
  },
  {
    icon: Sparkles,
    title: 'TRYRINO — 5% Off',
    text: 'Try Rino, our new senior therapist with 20+ years of international experience. New clients only.',
    highlight: 'Use code TRYRINO',
  },
  {
    icon: Shield,
    title: 'Pay Deposit — Extra 5% Off',
    text: 'Secure your slot with a small online deposit and save an extra 5% — stacks with your first-visit discount for 10% total.',
    highlight: 'Up to 10% total savings',
  },
];

const OfferPage = () => {
  const [bookingOpen, setBookingOpen] = useState(false);

  useEffect(() => {
    trackPixel('ViewContent', { content_name: 'Offer Landing Page', content_category: 'ad_landing' });
  }, []);

  return (
    <div className="min-h-screen bg-luxury-green-900" data-testid="offer-page">
      <Seo
        title="5% Off Your First Massage | Dublin Holistic Massage"
        description="New client offer: 5% off your first massage at Dublin Holistic Massage — deep tissue, sports & relaxing massage in Dublin 1 & Dublin 17. Book online in 2 minutes."
      />

      <div className="max-w-5xl mx-auto px-6 sm:px-8 py-16 sm:py-24">
        <div className="text-center mb-14">
          <span className="inline-flex items-center gap-2 bg-luxury-green-800 border border-luxury-gold/40 text-luxury-gold px-5 py-2 rounded-full text-xs uppercase tracking-[0.25em] font-semibold mb-8">
            <Sparkles size={14} />
            New Client Offer
          </span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-alabaster-100 leading-tight mb-6">
            Your First Massage,
            <span className="block italic text-luxury-gold mt-2">Up to 10% Off</span>
          </h1>
          <p className="text-alabaster-300 font-light max-w-xl mx-auto mb-8">
            Professional deep tissue, sports and relaxing massage by qualified therapists with 14+ years of experience.
            Two Dublin locations. Open 7 days, 9am–9pm.
          </p>

          <Button
            onClick={() => setBookingOpen(true)}
            className="bg-luxury-gold hover:bg-luxury-gold-600 text-luxury-green-900 rounded-full py-7 px-12 text-sm uppercase tracking-wider font-bold group transition-all"
            data-testid="offer-book-btn"
          >
            Book Now — Takes 2 Minutes
            <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
          </Button>

          <div className="flex items-center justify-center gap-2 mt-6">
            <div className="flex">
              {[1, 2, 3, 4, 5].map((n) => (
                <Star key={`star-${n}`} size={16} className="fill-luxury-gold text-luxury-gold" />
              ))}
            </div>
            <span className="text-alabaster-300 text-sm">5.0 on Google & TripAdvisor · 1000+ happy clients</span>
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-5 mb-14">
          {OFFERS.map((o) => (
            <div
              key={o.title}
              className="bg-luxury-green-800/70 border border-luxury-gold/25 rounded-3xl p-7 hover:border-luxury-gold/60 transition-colors"
              data-testid={`offer-card-${o.title.split(' ')[0].toLowerCase().replace(/[^a-z]/g, '')}`}
            >
              <o.icon size={26} className="text-luxury-gold mb-4" />
              <h3 className="font-display text-xl text-alabaster-100 mb-2">{o.title}</h3>
              <p className="text-alabaster-300 text-sm font-light leading-relaxed mb-4">{o.text}</p>
              <span className="inline-block bg-luxury-gold/15 text-luxury-gold text-xs font-semibold px-3 py-1 rounded-full">
                {o.highlight}
              </span>
            </div>
          ))}
        </div>

        <div className="grid sm:grid-cols-3 gap-4 text-center mb-14">
          <div className="flex flex-col items-center gap-2">
            <MapPin size={20} className="text-luxury-gold" />
            <p className="text-alabaster-100 text-sm font-medium">Two Locations</p>
            <p className="text-alabaster-300/70 text-xs">Talbot St (Dublin 1) & Clarehall (Dublin 17)</p>
          </div>
          <div className="flex flex-col items-center gap-2">
            <Clock size={20} className="text-luxury-gold" />
            <p className="text-alabaster-100 text-sm font-medium">Open 7 Days</p>
            <p className="text-alabaster-300/70 text-xs">9am – 9pm, book online anytime</p>
          </div>
          <div className="flex flex-col items-center gap-2">
            <Check size={20} className="text-luxury-gold" />
            <p className="text-alabaster-100 text-sm font-medium">Insurance Accepted</p>
            <p className="text-alabaster-300/70 text-xs">Receipts provided for health insurance</p>
          </div>
        </div>

        <div className="text-center">
          <Button
            onClick={() => setBookingOpen(true)}
            className="bg-luxury-gold hover:bg-luxury-gold-600 text-luxury-green-900 rounded-full py-7 px-12 text-sm uppercase tracking-wider font-bold"
            data-testid="offer-book-btn-bottom"
          >
            Claim Your Discount — Book Now
          </Button>
          <p className="mt-6">
            <a href="/" className="text-alabaster-300/60 hover:text-luxury-gold text-xs underline transition-colors">
              Visit our full website
            </a>
          </p>
        </div>
      </div>

      <BookingCalendar isOpen={bookingOpen} onClose={() => setBookingOpen(false)} />
    </div>
  );
};

export default OfferPage;
